<template>
  <div class="main-box new-plans-box" v-loader.opacity="loading">
    <div class="alert alert-danger" role="alert" v-if="shouldShowIncompleteNfeWarning">
      <h5>{{ $t('plans.nfe.title') }}</h5>

      <div class="">
        <p>{{ $t('plans.nfe.text') }}</p>
        <a href="#" @click="openPlansClinicDataOffcanvas">{{ $t('plans.nfe.link') }}</a>
      </div>
    </div>

    <div class="alert alert-warning" role="alert" v-if="needsResubscription">
      <h5>{{ $t('plans.resubscription.title') }}</h5>

      <div class="">
        <p>{{ $t('plans.resubscription.text') }}</p>
        <a href="#" v-if="isOwner" @click="declineMigration">{{ $t('plans.resubscription.declineButton') }}</a>
      </div>
    </div>

    <div class="alert alert-warning" role="alert" v-if="hasPaymentIssue">
      <p>{{ $t(!isMigrated ? 'plans.paymentIssue.textMigrated' : 'plans.paymentIssue.text') }}</p>
    </div>

    <div class="" v-if="!subscriptionName && false">
      <h1>{{ $t('plans.upgradePlanTitle') }}</h1>
      <p>{{ $t('plans.upgradePlanSubtitle') }}</p>
    </div>

    <div class="current-plan-box" v-else>
      <div class="current-plan-box-line">
        <div class="text-content">
          <p>{{ $t('plans.myPlan') }}</p>
          <div class="current-plan-total-images">
            <p>
              <span class="label" :class="subscriptionName ? 'label-success' : 'label-secondary'">
                {{ displayName || $t('plans.free') }}
              </span>
              {{ activePlanLimitText }} {{ $t('plans.imagesLabel') }}
            </p>
            <small class="text-danger" v-if="isExpired && needsResubscription">{{ $t('plans.planRevertedNotice') }}</small>
          </div>
        </div>
        <div class="action-content" v-if="isOwner && subscriptionName && subscriptionName !== 'Starter'">
          <button class="btn btn-outline-secondary" type="button" name="button" @click="handleCancelClick" v-if="subscriptionName && subscriptionDaysRemaining == null">{{ $t('plans.cancelPlan') }}</button>
        </div>
      </div>

      <div class="current-plan-box-line" v-if="subscriptionName && subscriptionName !== 'Starter'">
        <div class="text-content">
          <p>{{ $t('plans.billing') }}</p>
          <div class="section-billing">
            <p>
              <s v-if="activeDiscount" class="coupon-original-price">{{ couponOriginalPriceFormatted }}</s>
              {{ activeSubscriptionPriceValue }}
            </p>
            <small v-if="activeDiscount" class="coupon-badge">
              {{ $t('plans.couponLabel', { name: activeDiscount.name }) }}
              <template v-if="couponValidUntilFormatted">· {{ $t('plans.couponValidUntil', { date: couponValidUntilFormatted }) }}</template>
            </small>
            <p>{{ activeCardInfo }}</p>
            <small class="text-danger" v-if="cancellationStatusMessage">{{ cancellationStatusMessage }}</small>
            <small v-else-if="!isMigrated">{{ $t('plans.validUntil') }} {{ nextBillingDateFormatted }}</small>
            <small v-else>{{ $t('plans.nextBilling') }} {{ nextBillingDateFormatted }}</small>
          </div>
        </div>

        <div class="action-content">
          <button class="btn btn-outline-secondary" type="button" name="button" @click="toDeatilsPaymentHistory()" v-if="false">{{ $t('plans.payments') }}</button>
          <button class="btn btn-success" type="button" name="button" @click="openPlansPaymentMethodUpdateOffcanvas()" v-if="isOwner && isMigrated && subscriptionName && subscriptionDaysRemaining == null">{{ $t('plans.updatePayment') }}</button>
        </div>
      </div>

      <div class="current-plan-box-line" id="section-warning">
        <div class="text-content">
          <p>{{ $t('plans.planUsage') }}</p>

          <div class="progress-container-wrapper">
            <div class="progress-box">
              <p>{{ usageTextFormatted }}</p>
              <div class="progress" role="progressbar" aria-label="Basic example" :aria-valuenow="usagePercentage" aria-valuemin="0" aria-valuemax="100">
                <div class="progress-bar" :class="getPercentageClass()" :style="`width: ${usagePercentage}%`"></div>
              </div>
            </div>
            <p class="limit-exceeded-text danger" v-if="usagePercentage >= 100" v-html="$t('plans.limitExceededMessage')"></p>
          </div>
        </div>
      </div>
    </div>

    <div class="periodicity-box" :class="{'has-subscription' : subscriptionName}">
      <h1 v-if="subscriptionName">{{ $t('plans.comparePlans') }}</h1>

      <div class="btn-group" role="group">
        <input class="btn-check" type="radio" name="periodicity" id="monthly" value="month" v-model="billingCycle"/>
        <label class="btn btn-outline-secondary" for="monthly">{{ $t('plans.monthly') }}</label>
        <input class="btn-check" type="radio" name="periodicity" id="yearly" value="year" v-model="billingCycle"/>
        <label class="btn btn-outline-secondary" for="yearly">
          {{ $t('plans.yearly') }}&nbsp;
          <strong>{{ $t('plans.yearlyDiscount', { percent: formattedYearlyDiscountPercent }) }}</strong>
        </label>
      </div>
    </div>

    <div class="plans">
      <div class="plan-card">
        <div class="">
          <p class="label label-secondary" v-show="!subscriptionName">{{ $t('plans.current') }}</p>
          <h2>{{ $t('plans.free') }}</h2>
          <h1>{{ formattedCapturesLimit(10000) }}</h1>
          <h3>{{ $t('plans.capturesLabel') }}</h3>

          <button class="btn btn-outline-secondary disabled"
                  type="button"
                  v-if="!subscriptionName">
            {{ $t('plans.planCurrent') }}
          </button>
        </div>

        <div class="text-center">
          <p class="paragraph">
            {{ $t('plans.tierPlanInfo', { limit: formattedCapturesLimit(10000) }) }}
          </p>
        </div>
      </div>

      <template v-for="item in publicPlans" :key="item.name">
        <div class="plan-card"
             :class="{
                'active': isExactActivePlan(item) || (item.name === recommendedPlanName),
                'canceled': isExactActivePlan(item) && subscriptionDaysRemaining != null}">
          <div>
            <p class="label label-success" v-if="item.name === recommendedPlanName && subscriptionName !== item.name">{{ $t('plans.recommended') }}</p>
            <p class="label label-success" v-if="subscriptionName == item.name && subscriptionDaysRemaining == null">{{ $t('plans.current') }}</p>

            <h2>{{ item.name }}</h2>

            <h1>{{ formattedCapturesLimit(item.metadata.captures_limit) }}</h1>
            <h3>{{ $t('plans.capturesLabel') }}</h3>

            <template v-if="subscriptionName && subscriptionName !== 'Starter'">
              <button class="btn btn-outline-secondary disabled" type="button"
                      v-if="isExactActivePlan(item) && subscriptionDaysRemaining == null && !hasPaymentIssue">
                {{ $t('plans.planCurrent') }}
              </button>

              <button class="btn btn-success" type="button"
                      v-else-if="isExactActivePlan(item) && hasPaymentIssue && isOwner"
                      @click="openPlansCheckoutOffcanvas(item)">
                {{ $t('plans.signUp') }}
              </button>

              <button class="btn btn-success" type="button"
                      v-else-if="isExactActivePlan(item) && subscriptionDaysRemaining != null && isOwner"
                      @click="reactivateSubscription(item)">
                {{ $t(isMigrated ? 'plans.reactivate' : 'plans.signUp') }}
              </button>

              <template v-else-if="isOwner">
                <button class="btn btn-warning" type="button"
                        v-if="isCurrencyMismatch(item)"
                        @click="helpcenterUrl()">
                  {{ $t('plans.contactSupport') }}
                </button>

                <button v-else class="btn"
                        :class="item.name === recommendedPlanName ? 'btn-success' : 'btn-outline-success'"
                        type="button"
                        @click="handlePlanAction(item)">
                  {{ $t('plans.signUp') }}
                </button>
              </template>

            </template>

            <template v-else>
              <button class="btn"
                      :class="item.name === recommendedPlanName ? 'btn-success' : 'btn-outline-success'"
                      type="button"
                      v-if="isOwner || isAdmin"
                      @click="handleFreshCheckout(item)">
                {{ $t('plans.signUp') }}
              </button>
            </template>

            <template v-if="billingCycle === 'year' && item.name !== 'Corporate'">
              <p class="paragraph info">
                {{ $t('plans.yearlyTotalCash') }}
                <strong> {{ formatPriceByMonth(item) }}</strong>
              </p>

              <div class="yearly-plan-box">
                <h2>{{ formatYearlyPriceDivided(item) }} <small style="font-size: 0.5em">{{ $t('plans.perMonth') }}</small></h2>

                <p>-{{ $t('plans.percentDiscount', { percent: formattedYearlyDiscountPercent }) }}</p>
              </div>
            </template>

            <template v-else>
              <p class="paragraph info" @click="switchToYearly">
                {{ $t('plans.saveAction', { percent: formattedYearlyDiscountPercent }) }} <a class="fw-bolder" href="#">{{ $t('plans.annualPlanHere') }}</a>
              </p>

              <div>
                <h2>{{ formatPriceByMonth(item) }}</h2>
                <p class="paragraph">
                  {{ $t('plans.perMonth') }}
                </p>
              </div>
            </template>
          </div>

          <div class="text-center">
            <!-- <p class="paragraph">{{ $t(`plans.${item.metadata.i18n_info_key}`) }}</p> -->
            <p class="paragraph">
              {{ $t('plans.tierPlanInfo', { limit: formattedCapturesLimit(Number(item.metadata.captures_limit) + 10000) }) }}
            </p>
            <p v-if="item.metadata.i18n_cost_key" class="paragraph">
              {{ $t(`plans.${item.metadata.i18n_cost_key}`, { costValue: calculateAndFormatCostPerImage(item) }) }}
            </p>
          </div>
        </div>
      </template>

      <template v-if="customClinicPlan">
        <div class="plan-card"
            :class="{'active': (subscriptionName == customClinicPlan.name) || (customClinicPlan.name === recommendedPlanName),
                      'canceled': subscriptionName == customClinicPlan.name && subscriptionDaysRemaining != null}">
          <div>
            <p class="label label-success" v-if="subscriptionName == customClinicPlan.name && subscriptionDaysRemaining == null">{{ $t('plans.current') }}</p>

            <h2>Corporate</h2>

            <h1>{{ formattedCapturesLimit(customClinicPlan.metadata.captures_limit) }}</h1>
            <h3>{{ $t('plans.capturesLabel') }}</h3>

            <template v-if="subscriptionName && subscriptionName !== 'Starter'">
              <button class="btn btn-outline-secondary disabled" type="button" v-if="subscriptionName == customClinicPlan.name">
                {{ $t('plans.planCurrent') }}
              </button>

              <template v-else-if="isOwner">
                <button class="btn btn-warning" type="button"
                        v-if="isCurrencyMismatch(customClinicPlan)"
                        @click="helpcenterUrl()">
                  {{ $t('plans.contactSupport') }}
                </button>

                <button v-else class="btn btn-success" type="button" @click="openPlansUpdateOffcanvas(customClinicPlan)">
                  {{ $t('plans.signUp') }}
                </button>
              </template>
            </template>

            <template v-else>
              <button class="btn btn-success" type="button" v-if="isOwner || isAdmin" @click="openPlansCheckoutOffcanvas(customClinicPlan)">
                {{ $t('plans.signUp') }}
              </button>
            </template>

            <template v-if="billingCycle === 'year'">
              <p class="paragraph info">
                {{ $t('plans.yearlyTotalCash') }}
                <strong> {{ formatPriceByMonth(customClinicPlan) }}</strong>
              </p>

              <div class="yearly-plan-box">
                <h2>{{ formatYearlyPriceDivided(customClinicPlan) }} <small style="font-size: 0.5em">{{ $t('plans.perMonth') }}</small></h2>

                <p>-{{ $t('plans.percentDiscount', { percent: formattedYearlyDiscountPercent }) }}</p>
              </div>
            </template>

            <template v-else>
              <div>
                <h2>{{ formatPriceByMonth(customClinicPlan) }}</h2>
                <p class="paragraph">
                  {{ $t('plans.perMonth') }}
                </p>
              </div>
            </template>
          </div>

          <div class="text-center">
            <p class="paragraph">{{ $t('plans.customPlanDescription') }}</p>
          </div>
        </div>
      </template>

      <div class="plan-card" v-if="!customClinicPlan"
          :class="{
            'active': recommendedPlanName === 'Corporate',
            'canceled': subscriptionName === 'Corporate' && subscriptionDaysRemaining != null
          }">
        <div class="">
          <p class="label label-success" v-if="recommendedPlanName === 'Corporate'">{{ $t('plans.recommended') }}</p>

          <h2>Corporate</h2>
          <h1>{{ $t('plans.unlimited') }}</h1>
          <h3>&nbsp;</h3>

          <button class="btn btn-success" type="button" @click="leadForms()" name="button">
            {{ $t('plans.consult') }}
          </button>

          <h2>{{ $t('plans.consultPrice') }}</h2>
          <p class="paragraph">{{ $t('plans.personalizedPlan') }}</p>
        </div>

        <div class="text-center">
          <p class="paragraph">{{ $t('plans.corporatePlanInfo') }}</p>
        </div>
      </div>
    </div>

    <div class="plans-disclaimer">
      <p>{{ $t('plans.disclaimer.text') }}</p>
      <a :href="disclaimerUrl()" target="_blank">{{ $t('plans.disclaimer.link') }}</a>
    </div>

    <teleport to="body">
      <PlansCancelOffcanvas :offcanvas_id="'PlansCancelOffcanvas'" ref="plansCancelOffcanvas"/>
      <PlansCheckoutOffcanvas :offcanvas_id="'PlansCheckoutOffcanvas'" ref="plansCheckoutOffcanvas"/>
      <PlansClinicDataOffcanvas :offcanvas_id="'PlansClinicDataOffcanvas'" ref="plansClinicDataOffcanvas"/>
      <PlansPaymentMethodUpdateOffcanvas :offcanvas_id="'PlansPaymentMethodUpdateOffcanvas'" ref="plansPaymentMethodUpdateOffcanvas"/>
      <PlansUpdateOffcanvas :offcanvas_id="'PlansUpdateOffcanvas'" ref="plansUpdateOffcanvas"/>
      <BaseGenericOffcanvas :offcanvas_id="'PlansMigrationNoticeOffcanvas'" ref="plansMigrationNoticeOffcanvas"/>
    </teleport>
  </div>
</template>

<style lang="scss" src="./new-plans.scss" scoped></style>
<script src="./new-plans.js"></script>
